/**
 * ALL active race plans (one per event, soonest race first) + per-plan actions
 * — the multi-event replacement for the old singular `useActiveJourney`
 * (event-scoped-race-plans B2). Surfaces map over `journeys` (stacked rails,
 * one plans-card per event); actions live in `useJourneyActions` so each
 * rendered plan gets its own generate/preview/skip closures.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isAbortError } from "../../lib/api";
import { authPrincipalKey, useAuth, useAuthOptional } from "../../context/AuthProvider";
import { useToast } from "../../context/ToastProvider";
import {
  dropGenerateErrorCode,
  generateDrop,
  generateMilestoneKit as generateMilestoneKitApi,
  generateRaceDayPreview,
  getActiveJourneysWithLabels,
  JOURNEY_CHANGED_EVENT,
  journeyChangeStamp,
  kitGenerateErrorCode,
  notifyJourneyChanged,
  skipDrop,
  withJourneyRetries,
  type DropGenerateErrorCode,
  type JourneyBusy,
  type JourneyGenerateResult,
  type JourneyView,
  type KitGenerateErrorCode,
  type PlanGroupLabelEntry
} from "../../lib/journeyApi";

export type UseJourneys = {
  journeys: JourneyView[];
  /** Archived/completed plan-group label map (§5c provenance filter) — [] until
   *  loaded or on an older server. */
  planGroupLabels: PlanGroupLabelEntry[];
  /** CustomerEvent ids whose race FuelJourney was ARCHIVED ("I stopped this",
   *  journey-focus §3.2) — hide from "Not set up yet" + calendar flags. A
   *  COMPLETED race keeps its historical marker and is never in this list.
   *  [] until loaded / older servers. */
  suppressedGoalEventIds: string[];
  loading: boolean;
  /** Set after retries are exhausted — surfaces show a retry row, never nothing. */
  error: string | null;
  refresh: () => void;
  /** The principal (`authPrincipalKey`) the EXPOSED rows belong to — always the
   *  current principal, because rows produced under a different principal are
   *  withheld (empty list + `loading`) until the current one's fetch settles.
   *  Downstream focus resolution compares this against its own principal so a
   *  stale prop capture can never pass another account's rows off as settled
   *  data. Null outside a provider / before auth resolves. */
  dataPrincipalKey: string | null;
};

// Stable empties for the withheld (cross-principal) exposure — referential
// identity keeps consumer memos/effects from churning every render.
const NO_JOURNEYS: JourneyView[] = [];
const NO_LABELS: PlanGroupLabelEntry[] = [];
const NO_SUPPRESSED: string[] = [];

// Session-scoped snapshot of the last successful journeys fetch, KEYED BY
// PRINCIPAL (customer id / anon session — both own their own journeys). A
// newly-mounted consumer seeds its INITIAL data from this ONLY when the key
// matches the current principal, so it can paint immediately instead of waiting
// on a cold round-trip — e.g. the wizard's step-0 "go straight there" banner,
// opened on top of a screen (My Fuel / My Training) that already loaded
// journeys. The principal can flip in-place (a `refreshCsrf` re-ping after a
// cross-tab sign-in), so the key stops one principal seeding another's data.
// Remounts within {@link JOURNEYS_FRESH_MS} reuse the snapshot WITHOUT a
// network round-trip (SPA tab switches); `JOURNEY_CHANGED` and a stale TTL
// still force a refetch. The change-stamp guards mid-flight races.
type JourneysSnapshot = {
  principalKey: string;
  journeys: JourneyView[];
  planGroupLabels: PlanGroupLabelEntry[];
  suppressedGoalEventIds: string[];
  fetchedAt: number;
  /** `journeyChangeStamp()` at the moment this payload was accepted. */
  stamp: number;
};
let lastSnapshot: JourneysSnapshot | null = null;

/** How long a successful fetch stays "fresh" for remounts / visibility. */
const JOURNEYS_FRESH_MS = 60_000;

function isSnapshotFresh(snap: JourneysSnapshot): boolean {
  if (journeyChangeStamp() > snap.stamp) return false;
  return Date.now() - snap.fetchedAt < JOURNEYS_FRESH_MS;
}

// Dedupe concurrent mount fetches (My Training + JourneyBands +
// My Fuel can all mount in the same tick). Each in-flight request carries its
// IDENTITY — the principal it was started for and the change-stamp at start —
// because a bare shared promise is only safe to reuse for the same world it
// was requested in: a `notifyJourneyChanged` mid-flight (wizard apply →
// navigate) re-runs the effect, and reusing the PRE-change request would hand
// it a stale payload whose start-stamp equals the new run's (so the stale
// guard below can't catch it); likewise an anon→customer flip mid-flight must
// not serve the old principal's journeys to the new one.
type InFlightJourneysFetch = {
  promise: Promise<{
    journeys: JourneyView[];
    planGroupLabels: PlanGroupLabelEntry[];
    suppressedGoalEventIds: string[];
  }>;
  principalKey: string | null;
  /** `journeyChangeStamp()` when the request was started. */
  startedStamp: number;
};
let fetchInFlight: InFlightJourneysFetch | null = null;

/** Test helper — wipe the module cache between cases. */
export function resetJourneysCacheForTests(): void {
  lastSnapshot = null;
  fetchInFlight = null;
}

export function useJourneys(opts: { enabled?: boolean } = {}): UseJourneys {
  // `enabled: false` skips the fetch entirely — for consumers that only need
  // journeys conditionally (e.g. the edit modal's plan banner, which is
  // usually opened for non-plan workouts).
  const enabled = opts.enabled !== false;
  // Null outside a provider (isolated hook tests) or before auth resolves — then
  // we never seed from or write to the shared snapshot, so those paths behave
  // exactly as the un-cached hook did.
  const auth = useAuthOptional();
  const principalKey = auth ? authPrincipalKey(auth.state) : null;
  const seed =
    enabled && principalKey && lastSnapshot?.principalKey === principalKey
      ? lastSnapshot
      : null;
  const seedFresh = seed != null && isSnapshotFresh(seed);
  const [journeys, setJourneys] = useState<JourneyView[]>(() => seed?.journeys ?? []);
  const [planGroupLabels, setPlanGroupLabels] = useState<PlanGroupLabelEntry[]>(
    () => seed?.planGroupLabels ?? []
  );
  const [suppressedGoalEventIds, setSuppressedGoalEventIds] = useState<string[]>(
    () => seed?.suppressedGoalEventIds ?? []
  );
  // Principal whose fetch (or seed) produced the CURRENT hook-local rows.
  // Initialised to the mount principal: the initial state is either that
  // principal's seed or an empty list, both safely attributable to it. The
  // principal can then flip IN PLACE (a `refreshCsrf` re-ping after a
  // cross-tab sign-in) — the exposure below compares this against the live
  // principal so account A's rows are never rendered to account B, not even
  // for the beat before the new principal's effect settles.
  const [dataPrincipalKey, setDataPrincipalKey] = useState<string | null>(() => principalKey);
  // Ref mirror of `dataPrincipalKey` for effect closures (the fetch effect
  // must not re-run when the stamp changes): lets the error path tell a
  // same-principal refetch failure (keep the last rows painted) from a
  // first-fetch failure after a principal flip (the retained rows are another
  // principal's and must be CLEARED, not error-retained).
  const dataPrincipalRef = useRef<string | null>(principalKey);
  // Fresh snapshot → paint immediately, no loading shimmer on SPA tab switches.
  const [loading, setLoading] = useState(() => enabled && !seedFresh);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);
  // Change-stamp of the moment the current fetch STARTED (null = never
  // fetched). A `notifyJourneyChanged` can land while that fetch is in flight
  // but before this hook can hear the event — e.g. wizard apply fires notify →
  // navigates → this surface mounts and its first fetch races the dispatch —
  // and the resolved payload then predates the change. Comparing stamps closes
  // that window: the hook refetches instead of serving the pre-create list.
  const fetchStampRef = useRef<number | null>(null);

  useEffect(() => {
    const onChanged = () => refresh();
    window.addEventListener(JOURNEY_CHANGED_EVENT, onChanged);
    // Attaching late: if a change was announced after our current fetch
    // started, that fetch may be stale — refetch now that we can hear events.
    if (fetchStampRef.current != null && journeyChangeStamp() > fetchStampRef.current) {
      refresh();
    }
    // A box's "generating → ready" flip can be missed while the tab is
    // backgrounded (poll pauses). Reconcile when the document becomes visible
    // AGAIN — but only if our snapshot is stale / changed. Do NOT also listen
    // to `window` `focus`: that fires on every click back into the page and
    // made SPA tab switches + focus churn look like constant journey refetches.
    if (!enabled) {
      return () => window.removeEventListener(JOURNEY_CHANGED_EVENT, onChanged);
    }
    const onVisible = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const snap =
        principalKey && lastSnapshot?.principalKey === principalKey ? lastSnapshot : null;
      if (snap && isSnapshotFresh(snap)) return;
      refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(JOURNEY_CHANGED_EVENT, onChanged);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, enabled, principalKey]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    // Remount with a fresh snapshot (My Training ↔ My Fuel) — keep painted
    // data, skip the network. Forced refresh (reloadKey bump from
    // JOURNEY_CHANGED / stale visibility) always fetches.
    if (
      reloadKey === 0 &&
      principalKey &&
      lastSnapshot?.principalKey === principalKey &&
      isSnapshotFresh(lastSnapshot)
    ) {
      setJourneys(lastSnapshot.journeys);
      setPlanGroupLabels(lastSnapshot.planGroupLabels);
      setSuppressedGoalEventIds(lastSnapshot.suppressedGoalEventIds);
      dataPrincipalRef.current = principalKey;
      setDataPrincipalKey(principalKey);
      setLoading(false);
      setError(null);
      return;
    }

    const ac = new AbortController();
    const startStamp = journeyChangeStamp();
    fetchStampRef.current = startStamp;
    let refetching = false;
    setLoading(true);

    const run = (): Promise<{
      journeys: JourneyView[];
      planGroupLabels: PlanGroupLabelEntry[];
      suppressedGoalEventIds: string[];
    }> => {
      // Shared across concurrent mounts — do NOT tie to this effect's AbortSignal
      // or the first unmount would cancel siblings (My Training + JourneyBands).
      // Reuse ONLY a request with our identity (same principal, started at the
      // current change-stamp): a request begun before a mid-flight
      // `notifyJourneyChanged` — or under the previous principal — would
      // resolve pre-change data that the `journeyChangeStamp() > startStamp`
      // guard below cannot catch, because its start-stamp equals ours.
      const current = fetchInFlight;
      if (
        current &&
        current.principalKey === principalKey &&
        current.startedStamp >= startStamp
      ) {
        return current.promise;
      }
      const entry: InFlightJourneysFetch = {
        principalKey,
        startedStamp: startStamp,
        promise: withJourneyRetries(() => getActiveJourneysWithLabels()).finally(() => {
          // Only clear our OWN entry — a superseding request may already be
          // registered and must keep deduping. The superseded request is not
          // cancelled (siblings may still await it); it just stops being
          // reusable.
          if (fetchInFlight === entry) fetchInFlight = null;
        })
      };
      fetchInFlight = entry;
      return entry.promise;
    };

    run()
      .then(({ journeys: js, planGroupLabels: labels, suppressedGoalEventIds: suppressedIds }) => {
        if (ac.signal.aborted) return;
        if (principalKey) {
          // Stamp with the FETCH-START stamp, not the current one: if a change
          // landed mid-flight this payload predates it, and `isSnapshotFresh`
          // must report it stale so no consumer seeds it as current while the
          // refetch below is in flight.
          lastSnapshot = {
            principalKey,
            journeys: js,
            planGroupLabels: labels,
            suppressedGoalEventIds: suppressedIds,
            fetchedAt: Date.now(),
            stamp: startStamp
          };
        }
        setJourneys(js);
        setPlanGroupLabels(labels);
        setSuppressedGoalEventIds(suppressedIds);
        dataPrincipalRef.current = principalKey;
        setDataPrincipalKey(principalKey);
        setError(null);
        // A change landed mid-flight that our listener didn't trigger a
        // refetch for (it attached after the dispatch) — this payload may
        // predate it; fetch once more.
        if (journeyChangeStamp() > startStamp) {
          refetching = true;
          refresh();
        }
      })
      .catch((e: unknown) => {
        if (!isAbortError(e) && !ac.signal.aborted) {
          setError("Couldn't load your race plans.");
          // The failure belongs to THIS effect's principal — stamp it so the
          // error (not a permanent withheld/loading state) is what renders.
          // If the retained rows are ANOTHER principal's (flip → first fetch
          // failed), clear them: stamping alone would let the settled-error
          // exposure resurrect them.
          if (dataPrincipalRef.current !== principalKey) {
            setJourneys(NO_JOURNEYS);
            setPlanGroupLabels(NO_LABELS);
            setSuppressedGoalEventIds(NO_SUPPRESSED);
          }
          dataPrincipalRef.current = principalKey;
          setDataPrincipalKey(principalKey);
        }
      })
      .finally(() => {
        // Aborted runs (StrictMode remount / navigation) must not flip loading
        // off while a surviving sibling fetch is in flight (see useJourney) —
        // and neither must a stale run that just queued its own refetch, or
        // consumers would render the pre-change list as settled for a frame.
        if (!ac.signal.aborted && !refetching) setLoading(false);
      });
    return () => ac.abort();
  }, [reloadKey, enabled, principalKey, refresh]);

  // Principal flipped in place and the hook-local rows still belong to
  // ANOTHER principal: withhold them. The effect above is already fetching
  // for the new principal (principalKey is a dep); until that settles —
  // success or error — this hook exposes an EMPTY list as loading, never
  // another account's journeys. Internal state keeps the stale rows only as
  // garbage awaiting overwrite; they are unreachable from here.
  //
  // Rows stamped `null` (fetched before auth resolved) are the one exception:
  // they were requested with the SAME cookies the first resolved principal
  // names — a boot, not an account switch — so they paint rather than flash a
  // loading gap, and the refetch under the resolved principal overwrites them.
  if (dataPrincipalKey !== principalKey && dataPrincipalKey !== null) {
    return {
      journeys: NO_JOURNEYS,
      planGroupLabels: NO_LABELS,
      suppressedGoalEventIds: NO_SUPPRESSED,
      loading: enabled,
      error: null,
      refresh,
      dataPrincipalKey: principalKey
    };
  }
  return {
    journeys,
    planGroupLabels,
    suppressedGoalEventIds,
    loading,
    error,
    refresh,
    // The current principal, not the raw stamp: adopted boot rows (stamped
    // null above) are claimed for the principal that resolved over them.
    dataPrincipalKey: principalKey
  };
}

export type JourneyActions = {
  /** dropIndex currently generating (per-card spinners), "preview" for the
   *  goal race-day kit, or `milestone:{eventId}` for a tune-up kit (P5). */
  busy: JourneyBusy;
  /**
   * Last drop-generate failure, classified (journeys overhaul P1): the server
   * now distinguishes `empty_window` (422 — no fuel-eligible workouts in the
   * box's window; nudge "add workouts") from `drop_locked` (409) and
   * `drop_not_generatable` (404). `code: null` = unclassified (network/5xx).
   * Cleared on the next generate attempt. UI copy lands in a later lane.
   */
  generateError: { dropIndex: number; code: DropGenerateErrorCode | null } | null;
  /**
   * Last race-day KIT generate failure (goal preview or milestone), classified
   * like `generateError` so the container can toast a specific message instead
   * of one generic "couldn't build" for every failure mode. `key` mirrors the
   * busy value of the attempt; `code: null` = unclassified (network/5xx).
   * Cleared on the next preview/kit attempt.
   */
  kitError: { key: "preview" | `milestone:${string}`; code: KitGenerateErrorCode | null } | null;
  /** `email` (Contract C) is the anon lead email collected at the generate gate;
   *  omitted / ignored for customer principals (server reads `Customer.email`).
   *  `force` — rebuild a STALE box/kit: skips the server's slot-resident +
   *  idempotent reuse (same contract as `generateMilestoneKit`). Never set on
   *  first-time generates.
   *  `discardCustomizations` (F-11-1) — the customer confirmed a forced rebuild
   *  of a box carrying edits. Without it the server refuses such a rebuild
   *  (`409 source_has_customizations`) instead of clearing the edits silently. */
  generateDropPlan: (
    dropIndex: number,
    email?: string,
    force?: boolean,
    discardCustomizations?: boolean
  ) => Promise<JourneyGenerateResult | null>;
  generatePreview: (email?: string, force?: boolean) => Promise<JourneyGenerateResult | null>;
  /** Generate a MILESTONE tune-up's race-day kit (P5). Null on failure.
   *  `email` — anon lead capture, same contract as the other generates.
   *  `force` — rebuild a STALE kit (skips reuse the same way the goal preview's
   *  regenerate does; passed straight to the POST body). */
  generateMilestoneKit: (
    eventId: string,
    email?: string,
    force?: boolean
  ) => Promise<JourneyGenerateResult | null>;
  skip: (dropIndex: number) => Promise<void>;
};

/**
 * Generate / preview / skip for ONE race plan. Mutations broadcast
 * `JOURNEY_CHANGED_EVENT` so every journeys list (rail, cards, dock) refetches.
 */
export function useJourneyActions(journey: JourneyView | null): JourneyActions {
  const { state, refreshCsrf } = useAuth();
  const { push: pushToast } = useToast();
  const csrfToken = (state as { csrfToken?: string }).csrfToken ?? "";
  const [busy, setBusy] = useState<JourneyBusy>(null);
  const [generateError, setGenerateError] = useState<JourneyActions["generateError"]>(null);
  const [kitError, setKitError] = useState<JourneyActions["kitError"]>(null);

  // The session's CSRF token populates a beat after a cold boot; POSTing with
  // "" silently 403s. Guard every mutation and tell the user rather than
  // swallowing the click (Fix 3, mirrors the planner's generate bar).
  const csrfMissingToast = useCallback(() => {
    pushToast({
      tone: "info",
      message: "Just a moment — finishing sign-in",
      detail: "Try again in a second."
    });
  }, [pushToast]);

  const generateDropPlan = useCallback(
    async (
      dropIndex: number,
      email?: string,
      force?: boolean,
      discardCustomizations?: boolean
    ) => {
      if (!journey) return null;
      if (!csrfToken) {
        csrfMissingToast();
        return null;
      }
      setBusy(dropIndex);
      setGenerateError(null);
      try {
        const res = await generateDrop(
          journey.id,
          dropIndex,
          csrfToken,
          refreshCsrf,
          email,
          force,
          discardCustomizations
        );
        notifyJourneyChanged();
        return res;
      } catch (err) {
        // Surface the classified failure as state instead of an unhandled
        // rejection — callers already null-guard the result (`if (!res)`).
        setGenerateError({ dropIndex, code: dropGenerateErrorCode(err) });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [journey, csrfToken, refreshCsrf, csrfMissingToast]
  );

  const generatePreview = useCallback(
    async (email?: string, force?: boolean) => {
      if (!journey) return null;
      if (!csrfToken) {
        csrfMissingToast();
        return null;
      }
      setBusy("preview");
      setKitError(null);
      try {
        const res = await generateRaceDayPreview(journey.id, csrfToken, refreshCsrf, email, force);
        notifyJourneyChanged();
        return res;
      } catch (err) {
        // Sync dispatch / network failure — swallow so `void ...then()` callers
        // never see an unhandled rejection (Fix 2). Return null so the caller
        // null-guards; the classified code lets it toast a SPECIFIC message.
        setKitError({ key: "preview", code: kitGenerateErrorCode(err) });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [journey, csrfToken, refreshCsrf, csrfMissingToast]
  );

  // P5: a milestone kit generates like the goal preview but per event — the
  // busy value carries the eventId so only that milestone card spins.
  const generateMilestoneKit = useCallback(
    async (eventId: string, email?: string, force?: boolean) => {
      if (!journey) return null;
      if (!csrfToken) {
        csrfMissingToast();
        return null;
      }
      setBusy(`milestone:${eventId}`);
      setKitError(null);
      try {
        const res = await generateMilestoneKitApi(
          journey.id,
          eventId,
          csrfToken,
          refreshCsrf,
          email,
          force
        );
        notifyJourneyChanged();
        return res;
      } catch (err) {
        // 422 milestone_invalid / 404 / rate-limit / network — classified so
        // the container toasts a specific message; the view refetch renders
        // the truthful state.
        setKitError({ key: `milestone:${eventId}`, code: kitGenerateErrorCode(err) });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [journey, csrfToken, refreshCsrf, csrfMissingToast]
  );

  const skip = useCallback(
    async (dropIndex: number) => {
      if (!journey) return;
      if (!csrfToken) {
        csrfMissingToast();
        return;
      }
      await skipDrop(journey.id, dropIndex, csrfToken, refreshCsrf);
      notifyJourneyChanged();
    },
    [journey, csrfToken, refreshCsrf, csrfMissingToast]
  );

  return {
    busy,
    generateError,
    kitError,
    generateDropPlan,
    generatePreview,
    generateMilestoneKit,
    skip
  };
}
